import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './App.css';
import {
  getParts, getSections, getParagraphs, getPartParagraphs, getDetail, getParagraphById,
  search, saveReview, saveClauseReview, getReviews,
  adminUpdateClause, adminCreateClause,
  adminUpdateFact, adminCreateFact,
  getAnalysis, saveAnalysis, getExecSummary, saveExecSummary,
  getPrinciples, createPrinciple, updatePrinciple, deletePrinciple, acceptReview, acceptClauseReview,
  getAbout, postContact, getMessages, getUnreadCount, markMessageRead,
  getTags, createTag, getItemTags, setItemTags,
} from './api';
import { loginWithNostr, loginWithNsec, getToken, logout, authFetch } from './auth';

// ── Nostr utilities ───────────────────────────────────────────────────────────
const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
const BECH32_GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
function _b32Polymod(vs) {
  let c = 1;
  for (const v of vs) { const t = c >> 25; c = ((c & 0x1ffffff) << 5) ^ v; for (let i = 0; i < 5; i++) if ((t >> i) & 1) c ^= BECH32_GEN[i]; }
  return c;
}
function _b32HrpExp(hrp) { return [...hrp].map(c => c.charCodeAt(0) >> 5).concat([0], [...hrp].map(c => c.charCodeAt(0) & 31)); }
function hexToNpub(hex) {
  if (!hex || hex.length !== 64) return '';
  const bytes = hex.match(/.{2}/g).map(b => parseInt(b, 16));
  let acc = 0, bits = 0;
  const words = [];
  for (const b of bytes) { acc = (acc << 8) | b; bits += 8; while (bits >= 5) { bits -= 5; words.push((acc >> bits) & 31); } }
  if (bits > 0) words.push((acc << (5 - bits)) & 31);
  const hrp = 'npub';
  const chk = _b32Polymod([..._b32HrpExp(hrp), ...words, 0, 0, 0, 0, 0, 0]) ^ 1;
  const cs = [0,1,2,3,4,5].map(i => (chk >> (5 * (5 - i))) & 31);
  return hrp + '1' + [...words, ...cs].map(d => BECH32_CHARSET[d]).join('');
}

async function broadcastEvent(event) {
  const relays = ['wss://relay.damus.io', 'wss://nos.lol', 'wss://relay.nostr.band'];
  const msg = JSON.stringify(['EVENT', event]);
  let ok = 0;
  await Promise.allSettled(relays.map(url => new Promise(resolve => {
    try {
      const ws = new WebSocket(url);
      const t = setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 7000);
      ws.onopen = () => ws.send(msg);
      ws.onmessage = e => {
        try { if (JSON.parse(e.data)[0] === 'OK') { ok++; clearTimeout(t); ws.close(); resolve(); } } catch {}
      };
      ws.onerror = ws.onclose = () => resolve();
    } catch { resolve(); }
  })));
  if (!ok) throw new Error('Could not reach any Nostr relay — please try again.');
}

// ── About modal ───────────────────────────────────────────────────────────────
function AboutModal({ onClose }) {
  const [pubkey,    setPubkey]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [copied,    setCopied]    = useState(false);
  const [dmText,    setDmText]    = useState('');
  const [dmSending, setDmSending] = useState(false);
  const [dmSent,    setDmSent]    = useState(false);
  const [dmError,   setDmError]   = useState('');
  const canDM = !!window.nostr?.nip04;

  useEffect(() => {
    getAbout().then(d => { if (d.pubkey) setPubkey(d.pubkey); }).finally(() => setLoading(false));
  }, []);

  const npub = pubkey ? hexToNpub(pubkey) : null;

  const copyNpub = () => {
    if (!npub) return;
    navigator.clipboard.writeText(npub);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendDM = async () => {
    if (!dmText.trim() || !pubkey) return;
    setDmSending(true); setDmError('');
    try {
      const encrypted = await window.nostr.nip04.encrypt(pubkey, dmText.trim());
      const event = { kind: 4, created_at: Math.floor(Date.now() / 1000), tags: [['p', pubkey]], content: encrypted };
      const signed = await window.nostr.signEvent(event);
      await broadcastEvent(signed);
      setDmSent(true); setDmText('');
    } catch (e) { setDmError(e.message); }
    finally { setDmSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card about-card" onClick={e => e.stopPropagation()}>
        <div className="about-header">
          <h2>About this app</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="about-section">
          <div className="about-app-name">DUAA Regulatory Analysis</div>
          <p className="about-text">
            A tool for reviewing the <strong>Data (Use and Access) Act 2025</strong> (DUAA 2025), which received Royal Assent on 19 June 2025. Browse every clause and provision, add review comments flagging problems, questions and notes, define organisational principles, and generate an AI-powered analytical report — all in one place.
          </p>
        </div>

        <div className="about-divider" />

        <div className="about-section">
          <div className="about-section-title">Creator</div>
          <p className="about-text"><strong>Tweeds</strong></p>
        </div>

        {!loading && npub && (
          <>
            <div className="about-divider" />
            <div className="about-section">
              <div className="about-section-title">Find on Nostr</div>
              <div className="about-npub-row">
                <code className="about-npub">{npub.slice(0, 20)}…{npub.slice(-8)}</code>
                <button className="btn-ghost btn-sm" onClick={copyNpub}>
                  {copied ? '✓ Copied' : 'Copy npub'}
                </button>
                <a className="btn-ghost btn-sm about-link-btn"
                   href={`https://njump.me/${npub}`} target="_blank" rel="noopener noreferrer">
                  Open profile ↗
                </a>
              </div>
            </div>

            <div className="about-divider" />
            <div className="about-section">
              <div className="about-section-title">Send a message via Nostr</div>
              {canDM ? (
                dmSent ? (
                  <p className="about-text" style={{color:'var(--green)'}}>✓ Message sent!</p>
                ) : (
                  <>
                    <textarea className="about-dm-input" rows={3}
                      placeholder="Type your message…"
                      value={dmText} onChange={e => setDmText(e.target.value)} />
                    {dmError && <p className="about-dm-error">{dmError}</p>}
                    <button className="btn-primary btn-sm" onClick={sendDM}
                      disabled={dmSending || !dmText.trim()}>
                      {dmSending ? 'Sending…' : 'Send encrypted DM'}
                    </button>
                  </>
                )
              ) : (
                <p className="about-text">
                  Install a Nostr browser extension such as{' '}
                  <a className="about-link" href="https://getalby.com" target="_blank" rel="noopener noreferrer">Alby</a>{' '}
                  to send an encrypted direct message.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Contact modal ─────────────────────────────────────────────────────────────
function ContactModal({ onClose }) {
  const [name,    setName]    = useState('');
  const [handle,  setHandle]  = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const send = async () => {
    if (!message.trim()) { setError('Please enter a message.'); return; }
    setSending(true); setError('');
    try {
      await postContact({ name, contact_handle: handle, message });
      setDone(true);
    } catch (e) { setError(e.message || 'Failed to send. Please try again.'); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card contact-modal" onClick={e => e.stopPropagation()}>
        <h2>Contact Tweeds</h2>
        {done ? (
          <>
            <p className="contact-sent">Message sent — thanks!</p>
            <button className="btn-primary" style={{width:'100%',marginTop:16}} onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <div className="contact-field">
              <label>Name <span className="contact-optional">(optional)</span></label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div className="contact-field">
              <label>Contact handle <span className="contact-optional">(optional — email, phone, Nostr npub…)</span></label>
              <input value={handle} onChange={e => setHandle(e.target.value)} placeholder="So I can reply" />
            </div>
            <div className="contact-field">
              <label>Message</label>
              <textarea rows={5} value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Type your message…" autoFocus />
            </div>
            {error && <div className="login-error">{error}</div>}
            <div style={{display:'flex',gap:10,marginTop:4}}>
              <button className="btn-primary" style={{flex:1,padding:'9px'}} onClick={send} disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </button>
              <button className="btn-ghost" style={{padding:'9px 16px'}} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Messages page (admin only) ────────────────────────────────────────────────
function MessagesPage({ onRead }) {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    getMessages()
      .then(setMessages)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleRead = async (m) => {
    const updated = await markMessageRead(m.id, !m.read);
    setMessages(prev => prev.map(x => x.id === m.id ? updated : x));
    onRead();
  };

  const fmt = ts => new Date(ts).toLocaleString('en-GB', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit',
  });

  const unread = messages.filter(m => !m.read).length;

  if (loading) return <div className="main"><p className="loading">Loading…</p></div>;
  if (error)   return <div className="main"><p className="placeholder">{error}</p></div>;

  return (
    <div className="main">
      <h2 style={{marginBottom:20}}>
        Messages ({messages.length}){unread > 0 && <span className="msg-badge msg-badge-page">{unread} unread</span>}
      </h2>
      {messages.length === 0 && <p className="placeholder">No messages yet.</p>}
      {messages.map(m => (
        <div key={m.id} className={`msg-card${m.read ? ' msg-read' : ' msg-unread'}`}>
          <div className="msg-meta">
            <span className="msg-date">{fmt(m.sent_at)}</span>
            {m.name           && <span className="msg-field"><strong>Name:</strong> {m.name}</span>}
            {m.contact_handle && <span className="msg-field"><strong>Contact:</strong> {m.contact_handle}</span>}
            <button className={`msg-read-btn${m.read ? ' msg-read-btn-undo' : ''}`} onClick={() => toggleRead(m)}>
              {m.read ? '↩ Mark unread' : '✓ Mark as read'}
            </button>
          </div>
          <div className="msg-body">{m.message}</div>
        </div>
      ))}
    </div>
  );
}

// ── Login modal (Alby or nsec) ────────────────────────────────────────────────
function LoginModal({ onLogin, onClose }) {
  const [tab,     setTab]     = useState('nostr');
  const [nsec,    setNsec]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const attempt = async (fn) => {
    setLoading(true); setError('');
    try { await fn(); onLogin(); onClose(); }
    catch (e) { console.error('[LoginModal] attempt error:', e); setError(e.message); }
    finally   { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <h2>Login</h2>
        <div className="login-tabs">
          <button className={tab === 'nostr' ? 'active' : ''} onClick={() => setTab('nostr')}>Alby / NIP-07</button>
          <button className={tab === 'nsec'  ? 'active' : ''} onClick={() => setTab('nsec')}>nsec key</button>
        </div>

        {tab === 'nostr' && (
          <>
            <p className="login-tab-body">Use the Alby browser extension or any NIP-07 compatible wallet.</p>
            <button className="btn-primary" style={{width:'100%',padding:'9px'}}
              onClick={() => attempt(loginWithNostr)} disabled={loading}>
              {loading ? 'Connecting…' : 'Login with Nostr'}
            </button>
          </>
        )}

        {tab === 'nsec' && (
          <>
            <div className="nsec-warning">
              Your nsec is your Nostr private key and gives full identity control.
              Only enter it here because this app runs on your own server.
            </div>
            <input
              className="nsec-input"
              type="password"
              placeholder="nsec1…"
              value={nsec}
              onChange={e => setNsec(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && nsec && attempt(() => loginWithNsec(nsec))}
              autoFocus
            />
            <button className="btn-primary" style={{width:'100%',padding:'9px'}}
              onClick={() => attempt(() => loginWithNsec(nsec))}
              disabled={loading || !nsec.trim()}>
              {loading ? 'Signing in…' : 'Login'}
            </button>
          </>
        )}

        {error && <div className="login-error">{error}</div>}
      </div>
    </div>
  );
}

// ── Copy-link button ─────────────────────────────────────────────────────────
function CopyLinkButton() {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => {});
  };
  return (
    <button className={`btn-copy-link${copied ? ' btn-copy-link-done' : ''}`}
      onClick={copy} title="Click to copy URL to clipboard">
      {copied ? '✓ Copied' : '🔗 Link'}
    </button>
  );
}

// ── Review type/impact tag pills ─────────────────────────────────────────────
function ReviewTags({ type, impact }) {
  if (!type && !impact) return null;
  return (
    <span className="review-tags">
      {type   && <span className={`review-tag tag-type-${type.toLowerCase()}`}>Type: {type}</span>}
      {impact && <span className={`review-tag tag-impact-${impact.toLowerCase()}`}>Impact: {impact}</span>}
    </span>
  );
}

// ── Tag modal ─────────────────────────────────────────────────────────────────
function TagModal({ allTags, itemKey, itemTags, onClose, onSaved }) {
  const [localTags, setLocalTags] = useState(allTags);
  const [selected,  setSelected]  = useState(new Set((itemTags || []).map(t => t.tag_id)));
  const [newName,   setNewName]   = useState('');
  const [newColour, setNewColour] = useState('#4d9de0');
  const [creating,  setCreating]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState('');

  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true); setErr('');
    try {
      const tag = await createTag({ name: newName.trim(), colour: newColour });
      setLocalTags(prev => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setSelected(prev => new Set([...prev, tag.tag_id]));
      setNewName(''); setNewColour('#4d9de0');
    } catch (e) { setErr(e.message); }
    finally { setCreating(false); }
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      const [type, id] = itemKey.split(':');
      const tagRows = await setItemTags(type, parseInt(id), [...selected]);
      onSaved({ tagRows, allTags: localTags });
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box tag-modal">
        <div className="modal-title">Tags</div>
        <div className="tag-list-scroll">
          {localTags.length === 0 && <div className="tag-empty">No tags yet. Create one below.</div>}
          {localTags.map(t => (
            <label key={t.tag_id} className="tag-option">
              <input type="checkbox" checked={selected.has(t.tag_id)} onChange={() => toggle(t.tag_id)} />
              <span className="tag-pill" style={{ background: t.colour }}>{t.name}</span>
            </label>
          ))}
        </div>
        <div className="tag-create-row">
          <input className="tag-name-input" placeholder="New tag name…" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()} />
          <input type="color" className="tag-colour-picker" value={newColour}
            onChange={e => setNewColour(e.target.value)} />
          <button className="btn-sm btn-ghost" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? '…' : '+ Create'}
          </button>
        </div>
        {err && <div className="edit-err">{err}</div>}
        <div className="modal-actions">
          <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Apply'}
          </button>
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Tag zone: pills + button for one item ─────────────────────────────────────
function TagZone({ itemType, itemId, allTags, itemTagsMap, loggedIn, onTagsChanged }) {
  const [modalOpen, setModalOpen] = useState(false);
  const itemKey  = `${itemType}:${itemId}`;
  const itemTags = itemTagsMap[itemKey] || [];

  const handleSaved = ({ tagRows, allTags: newAllTags }) => {
    onTagsChanged(itemKey, tagRows, newAllTags);
    setModalOpen(false);
  };

  if (!loggedIn && itemTags.length === 0) return null;

  return (
    <span className="tag-zone">
      {itemTags.map(t => (
        <span key={t.tag_id} className="tag-pill" style={{ background: t.colour }}>{t.name}</span>
      ))}
      {loggedIn && (
        <button className="btn-tag" onClick={() => setModalOpen(true)} title="Tag this item">
          &#127991; Tag
        </button>
      )}
      {modalOpen && (
        <TagModal
          allTags={allTags}
          itemKey={itemKey}
          itemTags={itemTags}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </span>
  );
}

// ── Generic review editor ─────────────────────────────────────────────────────
function ReviewBlock({ review, onSave, onSaved, label = 'review', loggedIn = true, onAccept = null }) {
  const [editing,  setEditing]  = useState(false);
  const [draft,    setDraft]    = useState('');
  const [impact,   setImpact]   = useState('');
  const [type,     setType]     = useState('');
  const [saving,   setSaving]   = useState(false);
  const [saveErr,  setSaveErr]  = useState('');
  const [accepting,  setAccepting]  = useState(false);
  const [rejecting,  setRejecting]  = useState(false);

  const startEdit = () => { setDraft(review?.comment ?? ''); setImpact(review?.impact ?? ''); setType(review?.type ?? ''); setSaveErr(''); setEditing(true); };
  const cancel    = () => setEditing(false);

  const save = async (commentOverride, impactOverride, typeOverride) => {
    const comment = commentOverride !== undefined ? commentOverride : draft;
    const imp     = impactOverride  !== undefined ? impactOverride  : (impact || null);
    const typ     = typeOverride    !== undefined ? typeOverride    : (type   || null);
    setSaving(true); setSaveErr('');
    try {
      const result = await onSave(comment, imp, typ);
      if (result?.error) { setSaveErr(result.error); return; }
      onSaved(result.deleted ? null : result);
      setEditing(false);
    } catch (e) { setSaveErr(e.message); }
    finally { setSaving(false); }
  };

  const handleAccept = async () => {
    if (!onAccept) return;
    setAccepting(true); setSaveErr('');
    try {
      const result = await onAccept();
      if (result?.error) { setSaveErr(result.error); return; }
      onSaved(result);
    } catch (e) { setSaveErr(e.message); }
    finally { setAccepting(false); }
  };

  const handleReject = async () => {
    setRejecting(true); setSaveErr('');
    try {
      const result = await onSave('', null, null);
      if (result?.error) { setSaveErr(result.error); return; }
      onSaved(result?.deleted ? null : result);
    } catch (e) { setSaveErr(e.message); }
    finally { setRejecting(false); }
  };

  const formatDate = iso => new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  // Read-only view for non-logged-in users
  if (!loggedIn) {
    if (!review) return null;
    const isAI = !!review?.ai_generated;
    const impactClass = review?.impact ? `impact-${review.impact.toLowerCase()}` : 'impact-none';
    const typeClass   = review?.type   ? `type-${review.type.toLowerCase()}`     : '';
    return (
      <div className={`review-block${isAI ? ' ai-review' : ''} ${impactClass} ${typeClass}`}>
        {isAI && <span className="ai-badge">AI suggestion</span>}
        <div className="review-comment">{review.comment}</div>
        <div className="review-meta">
          <span>Reviewed {formatDate(review.updated_at)}</span>
          <ReviewTags type={review.type} impact={review.impact} />
        </div>
      </div>
    );
  }

  // AI-generated review — show accept/reject UI
  if (review?.ai_generated) {
    const impactClass = review.impact ? `impact-${review.impact.toLowerCase()}` : 'impact-none';
    const typeClass   = review.type   ? `type-${review.type.toLowerCase()}`     : '';
    return (
      <div className={`review-block ai-review ${impactClass} ${typeClass}`}>
        <span className="ai-badge">AI suggestion</span>
        <div className="review-comment">{review.comment}</div>
        {saveErr && <div style={{color:'#e05050',fontSize:'0.8rem',marginTop:4}}>{saveErr}</div>}
        <div className="review-meta">
          <ReviewTags type={review.type} impact={review.impact} />
          {loggedIn && (
            <div className="ai-review-actions">
              <button className="btn-accept btn-sm" onClick={handleAccept} disabled={accepting || rejecting}>
                {accepting ? '…' : '✓ Accept'}
              </button>
              <button className="btn-reject btn-sm" onClick={handleReject} disabled={accepting || rejecting}>
                {rejecting ? '…' : '✗ Reject'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="review-editor">
        <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)}
          placeholder="Enter your review comment…" />
        <div className="review-impact-row">
          <label className="review-impact-label">Type</label>
          <select className="review-impact-select" value={type} onChange={e => setType(e.target.value)}>
            <option value="">—</option>
            <option value="Problem">Problem</option>
            <option value="Question">Question</option>
            <option value="Note">Note</option>
          </select>
          <label className="review-impact-label" style={{marginLeft:12}}>Impact</label>
          <select className="review-impact-select" value={impact} onChange={e => setImpact(e.target.value)}>
            <option value="">—</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        {saveErr && <div style={{color:'#e05050',fontSize:'0.8rem',marginBottom:4}}>{saveErr}</div>}
        <div className="review-editor-actions">
          <button className="btn-primary btn-sm" onClick={() => save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn-ghost btn-sm" onClick={cancel}>Cancel</button>
          {review && <button className="btn-danger" onClick={() => save('', null, null)}>Delete review</button>}
        </div>
      </div>
    );
  }

  const impactClass = review?.impact ? `impact-${review.impact.toLowerCase()}` : 'impact-none';
  const typeClass   = review?.type   ? `type-${review.type.toLowerCase()}`     : '';

  if (review) {
    return (
      <div className={`review-block ${impactClass} ${typeClass}`}>
        <div className="review-comment">{review.comment}</div>
        <div className="review-meta">
          <span>Reviewed {formatDate(review.updated_at)}</span>
          <ReviewTags type={review.type} impact={review.impact} />
          <button className="btn-ghost btn-sm" onClick={startEdit}>Edit</button>
        </div>
      </div>
    );
  }

  return <button className="review-add" onClick={startEdit}>+ add {label}</button>;
}

// ── Clause edit / create form ─────────────────────────────────────────────────
function ClauseEditForm({ clause, paragraph, suggestedSortOrder, onSave, onCancel }) {
  const isNew = !clause;
  const [num,     setNum]     = useState(clause?.clause_number ?? '');
  const [text,    setText]    = useState(clause?.clause_text   ?? '');
  const [sortOrd, setSortOrd] = useState(String(clause?.sort_order ?? suggestedSortOrder ?? 1));
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  const handleSave = async () => {
    if (!num.trim()) return setErr('Clause number is required');
    setSaving(true); setErr('');
    try {
      const data = { clause_number: num.trim(), clause_text: text.trim(), sort_order: parseFloat(sortOrd) || 1 };
      const result = isNew
        ? await adminCreateClause({ ...data, paragraph_id: paragraph.paragraph_id, part_id: paragraph.part_id, section_id: paragraph.section_id })
        : await adminUpdateClause(clause.clause_id, data);
      onSave(result);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="edit-form">
      <div className="edit-form-title">{isNew ? 'Add clause' : `Edit clause (${clause.clause_number})`}</div>
      {err && <div className="edit-err">{err}</div>}
      <div className="edit-form-row"><label>Number</label>
        <input value={num} onChange={e => setNum(e.target.value)} placeholder="e.g. 1a" /></div>
      <div className="edit-form-row"><label>Text</label>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Clause text…" /></div>
      <div className="edit-form-row"><label>Sort order</label>
        <input type="number" step="0.01" value={sortOrd} onChange={e => setSortOrd(e.target.value)} style={{maxWidth:100}} /></div>
      <div className="edit-form-actions">
        <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : isNew ? 'Add' : 'Save'}</button>
        <button className="btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Fact edit / create form ───────────────────────────────────────────────────
function FactEditForm({ fact, paragraph, clauses, defaultClauseId, suggestedSortOrder, onSave, onCancel }) {
  const isNew = !fact;
  const [ident,    setIdent]    = useState(fact?.identifier     ?? '');
  const [text,     setText]     = useState(fact?.text           ?? '');
  const [provType, setProvType] = useState(fact?.provision_type ?? 'point');
  const [clauseId, setClauseId] = useState(String(fact?.clause_id ?? defaultClauseId ?? ''));
  const [sortOrd,  setSortOrd]  = useState(String(fact?.sort_order ?? suggestedSortOrder ?? 1));
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  const handleSave = async () => {
    if (!ident.trim()) return setErr('Identifier required');
    if (!text.trim())  return setErr('Text required');
    setSaving(true); setErr('');
    try {
      const data = {
        identifier: ident.trim(), text: text.trim(), provision_type: provType,
        clause_id: clauseId ? parseInt(clauseId, 10) : null,
        sort_order: parseFloat(sortOrd) || 1,
      };
      const result = isNew
        ? await adminCreateFact({ ...data, paragraph_id: paragraph.paragraph_id, part_id: paragraph.part_id, section_id: paragraph.section_id })
        : await adminUpdateFact(fact.fact_id, data);
      onSave(result);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="edit-form edit-form-fact">
      <div className="edit-form-title">{isNew ? 'Add fact' : `Edit (${fact.identifier})`}</div>
      {err && <div className="edit-err">{err}</div>}
      <div className="edit-form-row"><label>Identifier</label>
        <input value={ident} onChange={e => setIdent(e.target.value)} placeholder="a, b, i, …" style={{maxWidth:80}} /></div>
      <div className="edit-form-row"><label>Type</label>
        <select value={provType} onChange={e => setProvType(e.target.value)} style={{maxWidth:140}}>
          <option value="point">point</option>
          <option value="sub-point">sub-point</option>
        </select></div>
      <div className="edit-form-row"><label>Clause</label>
        <select value={clauseId} onChange={e => setClauseId(e.target.value)}>
          <option value="">— none —</option>
          {clauses.map(c => (
            <option key={c.clause_id} value={String(c.clause_id)}>
              ({c.clause_number}) {c.clause_text?.slice(0, 40)}{c.clause_text?.length > 40 ? '…' : ''}
            </option>
          ))}
        </select></div>
      <div className="edit-form-row"><label>Sort order</label>
        <input type="number" step="0.01" value={sortOrd} onChange={e => setSortOrd(e.target.value)} style={{maxWidth:100}} /></div>
      <div className="edit-form-row"><label>Text</label>
        <textarea value={text} onChange={e => setText(e.target.value)} /></div>
      <div className="edit-form-actions">
        <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : isNew ? 'Add' : 'Save'}</button>
        <button className="btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Paragraph detail ──────────────────────────────────────────────────────────
function ParagraphDetail({ paragraph, section, part, editMode, loggedIn, onNav, onContact, tags, itemTagsMap, onTagsChanged }) {
  const [clauses,       setClauses]       = useState(null);
  const [facts,         setFacts]         = useState(null);
  const [reviews,       setReviews]       = useState({});
  const [clauseReviews, setClauseReviews] = useState({});
  const [loading,       setLoading]       = useState(false);
  const [reviewedOnly,  setReviewedOnly]  = useState(false);

  const [editingClauseId,   setEditingClauseId]   = useState(null);
  const [addClauseAfterIdx, setAddClauseAfterIdx] = useState(null);
  const [editingFactId,     setEditingFactId]     = useState(null);
  const [addFactInClause,   setAddFactInClause]   = useState(null);

  useEffect(() => {
    if (!paragraph) return;
    setClauses(null); setFacts(null); setReviews({}); setClauseReviews({});
    setLoading(true); setReviewedOnly(false);
    setEditingClauseId(null); setAddClauseAfterIdx(null);
    setEditingFactId(null);   setAddFactInClause(null);
    getDetail(paragraph.paragraph_id).then(d => {
      setClauses(d.clauses.slice().sort((a, b) => a.sort_order - b.sort_order));
      setFacts(d.facts.slice().sort((a, b) => a.sort_order - b.sort_order));
      const rmap = {}; d.reviews.forEach(r => { rmap[r.fact_id] = r; }); setReviews(rmap);
      const crmap = {}; d.clauseReviews.forEach(r => { crmap[r.clause_id] = r; }); setClauseReviews(crmap);
      setLoading(false);
    });
  }, [paragraph]);

  const handleSaved = useCallback((factId, review) => {
    setReviews(prev => { const next = {...prev}; if (review) next[factId] = review; else delete next[factId]; return next; });
  }, []);

  const handleClauseReviewSaved = useCallback((clauseId, review) => {
    setClauseReviews(prev => { const next = {...prev}; if (review) next[clauseId] = review; else delete next[clauseId]; return next; });
  }, []);

  const handleClauseSaved = (updated) => {
    setClauses(prev => {
      const exists = prev.some(c => c.clause_id === updated.clause_id);
      const next = exists ? prev.map(c => c.clause_id === updated.clause_id ? updated : c) : [...prev, updated];
      return next.slice().sort((a, b) => a.sort_order - b.sort_order);
    });
    setEditingClauseId(null); setAddClauseAfterIdx(null);
  };

  const handleFactSaved = (updated) => {
    setFacts(prev => {
      const exists = prev.some(f => f.fact_id === updated.fact_id);
      const next = exists ? prev.map(f => f.fact_id === updated.fact_id ? updated : f) : [...prev, updated];
      return next.slice().sort((a, b) => a.sort_order - b.sort_order);
    });
    setEditingFactId(null); setAddFactInClause(null);
  };

  const getSuggestedClauseSortOrder = (afterIdx) => {
    if (afterIdx < 0 || !clauses.length) return 1;
    const before = clauses[afterIdx], after = clauses[afterIdx + 1];
    return after ? (before.sort_order + after.sort_order) / 2 : before.sort_order + 1;
  };

  const getSuggestedFactSortOrder = (clauseId) => {
    const cf = facts.filter(f => (f.clause_id ?? null) === (clauseId ?? null));
    return cf.length ? Math.max(...cf.map(f => f.sort_order)) + 1 : 1;
  };

  if (!paragraph) return (
    <div className="main">
      <div className="welcome-card">
        <p className="welcome-byline">This app by <strong>Tweeds</strong> is a tool for reviewing the Data (Use and Access) Act 2025 (DUAA 2025).</p>
        <div className="welcome-section">
          <h3>The Act</h3>
          <p>You can peruse or search through the Act parts and clauses using the menu to the left. Any <button className="inline-link" onClick={() => onNav('reviews')}>review comments</button> made against a clause will appear in-line with the Act text.</p>
        </div>
        <div className="welcome-section">
          <h3>The Review</h3>
          <p>The app has an embedded set of <button className="inline-link" onClick={() => onNav('pub-principles')}>architecture principles</button> against which the Act has been reviewed. This was a two-stage review with the first step AI-based using the latest Claude models, and the second step a series of manual reviews by industry and data management experts. Review comments are graded for their impact on the principles. Review comments appear in-line with the Act Parts, and also listed in a dedicated page.</p>
        </div>
        <div className="welcome-section">
          <h3>The Analysis Report</h3>
          <p>The <button className="inline-link" onClick={() => onNav('analysis')}>analysis report</button> pulls together the Act, Principles, and a summary of Reviews into a single page which brings to the surface the key Themes, Risks and Actions recommended by the Review.</p>
        </div>
        <div className="pub-principles-feedback">
          <h3>Have feedback on the review or the app?</h3>
          <p>
            We welcome input from practitioners, policy experts, and the public. If you'd like to
            comment on the review, suggest changes, or just get in touch, please use the link below.
          </p>
          <button className="btn-primary" onClick={onContact}>Contact / Feedback</button>
        </div>
      </div>
    </div>
  );
  if (loading || !clauses) return <div className="main"><p className="loading">Loading…</p></div>;

  const reviewCount = Object.keys(reviews).length + Object.keys(clauseReviews).length;
  const visibleFacts = reviewedOnly ? facts.filter(f => reviews[f.fact_id]) : facts;
  const factsByClause = {};
  visibleFacts.forEach(f => { const key = f.clause_id ?? 'none'; if (!factsByClause[key]) factsByClause[key] = []; factsByClause[key].push(f); });

  const renderFact = f => {
    if (editMode && editingFactId === f.fact_id) {
      return (
        <div key={f.fact_id} className="fact-row">
          <FactEditForm fact={f} paragraph={paragraph} clauses={clauses}
            onSave={handleFactSaved} onCancel={() => setEditingFactId(null)} />
        </div>
      );
    }
    return (
      <div key={f.fact_id} className="fact-row">
        <div className={`fact-item ${f.provision_type}`}>
          {editMode && (
            <span className="edit-badges">
              <span className="edit-badge">{f.fact_id}</span>
              <span className="edit-badge">so:{f.sort_order}</span>
              <span className="edit-badge">cl:{f.clause_id ?? '—'}</span>
              <button className="edit-btn" onClick={() => setEditingFactId(f.fact_id)}>✎</button>
            </span>
          )}
          <span className="fact-ident">({f.identifier})</span>
          <span className="fact-text">{f.text}</span>
        </div>
        <div className="fact-review-area">
          <ReviewBlock
            review={reviews[f.fact_id] ?? null}
            onSave={(comment, impact, type) => saveReview(f.fact_id, comment, impact, type)}
            onSaved={result => handleSaved(f.fact_id, result)}
            loggedIn={loggedIn}
            onAccept={() => acceptReview(f.fact_id)}
          />
          {tags != null && <TagZone itemType="fact" itemId={f.fact_id}
            allTags={tags} itemTagsMap={itemTagsMap} loggedIn={loggedIn} onTagsChanged={onTagsChanged} />}
        </div>
      </div>
    );
  };

  const renderClauseFacts = (cl) => {
    const clFacts = factsByClause[cl.clause_id] || [];
    if (clFacts.length === 0 && !editMode) return null;
    return (
      <div className="facts-list">
        {clFacts.map(renderFact)}
        {editMode && (
          addFactInClause === cl.clause_id ? (
            <FactEditForm paragraph={paragraph} clauses={clauses} defaultClauseId={cl.clause_id}
              suggestedSortOrder={getSuggestedFactSortOrder(cl.clause_id)}
              onSave={handleFactSaved} onCancel={() => setAddFactInClause(null)} />
          ) : (
            <button className="add-btn add-btn-fact" onClick={() => setAddFactInClause(cl.clause_id)}>
              + add fact in ({cl.clause_number})
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <div className="main">
      <div className="para-header">
        <div className="breadcrumb">{part?.part_number}{section?.section_title ? ` › ${section.section_title}` : ''}</div>
        <h2>{paragraph.paragraph_number}. {paragraph.paragraph_title}</h2>
        {editMode && (
          <div className="para-edit-meta">
            <span className="edit-badge">para_id:{paragraph.paragraph_id}</span>
            <span className="edit-badge">part_id:{paragraph.part_id}</span>
            <span className="edit-badge">section_id:{paragraph.section_id}</span>
          </div>
        )}
        <div className="para-toolbar">
          <span className="review-count">{reviewCount} review{reviewCount !== 1 ? 's' : ''}</span>
          {loggedIn && (reviewedOnly
            ? <button className="btn-ghost btn-sm" onClick={() => setReviewedOnly(false)}>Clear filter</button>
            : <button className="btn-ghost btn-sm" onClick={() => setReviewedOnly(true)} disabled={reviewCount === 0}>Show reviewed only</button>
          )}
          {tags != null && <TagZone itemType="paragraph" itemId={paragraph.paragraph_id}
            allTags={tags} itemTagsMap={itemTagsMap} loggedIn={loggedIn} onTagsChanged={onTagsChanged} />}
        </div>
      </div>

      {editMode && clauses.length === 0 && (
        addClauseAfterIdx === -1 ? (
          <ClauseEditForm paragraph={paragraph} suggestedSortOrder={1}
            onSave={handleClauseSaved} onCancel={() => setAddClauseAfterIdx(null)} />
        ) : (
          <button className="add-btn" onClick={() => setAddClauseAfterIdx(-1)}>+ Add first clause</button>
        )
      )}
      {!editMode && clauses.length === 0 && <p className="empty">No clauses found.</p>}

      {clauses.map((cl, idx) => (
        <div key={cl.clause_id}>
          {editMode && editingClauseId === cl.clause_id ? (
            <ClauseEditForm clause={cl} paragraph={paragraph}
              onSave={handleClauseSaved} onCancel={() => setEditingClauseId(null)} />
          ) : (
            <div className="clause">
              <div className="clause-header-row">
                <div className="clause-number">({cl.clause_number})</div>
                {editMode && (
                  <>
                    <span className="edit-badge">id:{cl.clause_id}</span>
                    <span className="edit-badge">so:{cl.sort_order}</span>
                    <button className="edit-btn" onClick={() => setEditingClauseId(cl.clause_id)}>✎</button>
                  </>
                )}
              </div>
              <div className="clause-text">{cl.clause_text}</div>
              <div className="clause-review-area">
                <ReviewBlock
                  review={clauseReviews[cl.clause_id] ?? null}
                  onSave={(comment, impact, type) => saveClauseReview(cl.clause_id, comment, impact, type)}
                  onSaved={result => handleClauseReviewSaved(cl.clause_id, result)}
                  label="clause review" loggedIn={loggedIn}
                  onAccept={() => acceptClauseReview(cl.clause_id)}
                />
                {tags != null && <TagZone itemType="clause" itemId={cl.clause_id}
                  allTags={tags} itemTagsMap={itemTagsMap} loggedIn={loggedIn} onTagsChanged={onTagsChanged} />}
              </div>
              {renderClauseFacts(cl)}
            </div>
          )}
          {editMode && (
            addClauseAfterIdx === idx ? (
              <ClauseEditForm paragraph={paragraph}
                suggestedSortOrder={getSuggestedClauseSortOrder(idx)}
                onSave={handleClauseSaved} onCancel={() => setAddClauseAfterIdx(null)} />
            ) : (
              <button className="add-btn" onClick={() => setAddClauseAfterIdx(idx)}>
                + add clause after ({cl.clause_number})
              </button>
            )
          )}
        </div>
      ))}

      {(factsByClause['none'] || []).length > 0 && (
        <div className="clause">
          <div className="clause-number">(provisions)</div>
          <div className="facts-list">{factsByClause['none'].map(renderFact)}</div>
        </div>
      )}
    </div>
  );
}

// ── Public principles page ────────────────────────────────────────────────────
function PrinciplesPublicPage({ onContact }) {
  const [principles, setPrinciples] = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    getPrinciples().then(ps => { setPrinciples(ps); setLoading(false); });
  }, []);

  const priorityOrder = { High: 0, Medium: 1, Low: 2 };
  const sorted = [...principles].sort(
    (a, b) => (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2) || a.sort_order - b.sort_order
  );

  if (loading) return <div className="main"><p className="loading">Loading…</p></div>;

  return (
    <div className="main">
      <div className="pub-principles-page">
        <h2 className="pub-principles-h2">Review Principles</h2>

        <p className="pub-principles-intro">
          These principles form a checklist for the regulatory assessment and are designed to evaluate
          laws, regulations, and policies. They prioritise human rights (privacy, free expression,
          association, and self-determination) through technical architecture.
        </p>

        <p className="pub-principles-intro">
          All clauses and points from the DUAA were reviewed against these Architecture Principles.
          The review identifies where the Act potentially conflicts with the stated principles. It was
          carried out in two stages: firstly an AI sweep using the latest Claude LLM, and secondly a
          series of detailed manual reviews by technical experts in the field of data management and
          privacy.
        </p>

        {principles.length === 0
          ? <p className="placeholder">No principles have been defined yet.</p>
          : (
            <div className="pub-principles-list">
              {sorted.map(p => (
                <div key={p.principle_id} className="pub-principle-card">
                  <div className="pub-principle-header">
                    <span className="pub-principle-title">{p.title}</span>
                    <span className={`principle-badge prio-${p.priority.toLowerCase()}`}>{p.priority}</span>
                  </div>
                  {p.description && <p className="pub-principle-desc">{p.description}</p>}
                </div>
              ))}
            </div>
          )
        }

        <div className="pub-principles-refs">
          <h3 className="pub-principles-refs-title">References</h3>
          <ol className="pub-principles-refs-list">
            <li>
              <a href="https://dl.acm.org/doi/10.1145/3785466" target="_blank" rel="noopener noreferrer">
                Self-Sovereign Identity as a Secure and Trustworthy Approach to Digital Identity Management
              </a>
            </li>
            <li>
              <a href="https://freetafa.com/" target="_blank" rel="noopener noreferrer">
                The Architecture of Autonomy
              </a>
            </li>
          </ol>
        </div>

        <div className="pub-principles-feedback">
          <h3>Have feedback on these principles?</h3>
          <p>
            We welcome input from practitioners, policy experts, and the public. If you'd like to
            suggest additions, amendments, or challenge the approach, please get in touch.
          </p>
          <button className="btn-primary" onClick={onContact}>Send feedback / Contact Tweeds</button>
        </div>
      </div>
    </div>
  );
}

// ── Search results ────────────────────────────────────────────────────────────
function SearchResults({ results, loading }) {
  if (loading) return <div className="main"><p className="loading">Searching…</p></div>;
  if (!results) return <div className="main"><p className="placeholder">Enter at least 3 characters to search.</p></div>;
  const { clauses = [], facts = [] } = results;
  const total = clauses.length + facts.length;
  return (
    <div className="main">
      <div className="search-results">
        <h2>{total} result{total !== 1 ? 's' : ''}</h2>
        {clauses.map(c => (
          <div key={`c-${c.clause_id}`} className="result-card">
            <div className="result-meta"><span className="tag clause">Clause</span>
              {c.part_number} · {c.section_title} · §{c.paragraph_number}({c.clause_number})</div>
            <div className="result-text">{c.clause_text}</div>
          </div>
        ))}
        {facts.map(f => (
          <div key={`f-${f.fact_id}`} className="result-card fact-result">
            <div className="result-meta">
              <span className={`tag ${f.provision_type === 'sub-point' ? 'subpoint' : 'point'}`}>{f.provision_type}</span>
              {f.part_number} · {f.section_title} · §{f.paragraph_number} ({f.identifier})</div>
            <div className="result-text">{f.text}</div>
          </div>
        ))}
        {total === 0 && <p className="empty">No results found.</p>}
      </div>
    </div>
  );
}

// ── All review comments ───────────────────────────────────────────────────────
function AllReviews({ loggedIn }) {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [filterType,     setFilterType]     = useState('');
  const [filterImpact,   setFilterImpact]   = useState('');
  const [filterReviewer, setFilterReviewer] = useState('');
  const [filterPart,     setFilterPart]     = useState('');
  const [filterSection,  setFilterSection]  = useState('');

  useEffect(() => { getReviews().then(d => { setData(d); setLoading(false); }); }, []);

  const handleFactSaved = useCallback((factId, updated) => {
    setData(prev => ({
      ...prev,
      factReviews: updated
        ? prev.factReviews.map(r => r.fact_id === factId ? { ...r, comment: updated.comment, impact: updated.impact, type: updated.type, updated_at: updated.updated_at, ai_generated: updated.ai_generated } : r)
        : prev.factReviews.filter(r => r.fact_id !== factId),
    }));
  }, []);

  const handleClauseReviewSaved = useCallback((clauseId, updated) => {
    setData(prev => ({
      ...prev,
      clauseReviews: updated
        ? prev.clauseReviews.map(r => r.clause_id === clauseId ? { ...r, comment: updated.comment, impact: updated.impact, type: updated.type, updated_at: updated.updated_at, ai_generated: updated.ai_generated } : r)
        : prev.clauseReviews.filter(r => r.clause_id !== clauseId),
    }));
  }, []);

  if (loading) return <div className="main"><p className="loading">Loading…</p></div>;
  const { factReviews = [], clauseReviews = [] } = data || {};
  if (!factReviews.length && !clauseReviews.length)
    return <div className="main"><p className="empty">No review comments yet.</p></div>;

  // Build part/section options for dropdowns with human/AI counts
  const partMap = {};
  const sectionCountMap = {}; // key: "part__section"
  [...factReviews, ...clauseReviews].forEach(r => {
    if (!partMap[r.part_number]) partMap[r.part_number] = { part_title: r.part_title, sections: new Set(), human: 0, ai: 0 };
    r.ai_generated ? partMap[r.part_number].ai++ : partMap[r.part_number].human++;
    if (r.section_title) {
      partMap[r.part_number].sections.add(r.section_title);
      const sk = `${r.part_number}__${r.section_title}`;
      if (!sectionCountMap[sk]) sectionCountMap[sk] = { human: 0, ai: 0 };
      r.ai_generated ? sectionCountMap[sk].ai++ : sectionCountMap[sk].human++;
    }
  });
  const partOptions = Object.keys(partMap).sort();
  const sectionOptions = filterPart ? [...(partMap[filterPart]?.sections ?? [])] : [];

  const matchesFilters = r =>
    (!filterType     || r.type === filterType) &&
    (!filterImpact   || (filterImpact === '=' ? !r.impact : r.impact === filterImpact)) &&
    (!filterReviewer || (filterReviewer === 'AI' ? r.ai_generated : !r.ai_generated)) &&
    (!filterPart     || r.part_number === filterPart) &&
    (!filterSection  || r.section_title === filterSection);

  const filteredFact   = factReviews.filter(matchesFilters);
  const filteredClause = clauseReviews.filter(matchesFilters);

  // Group into part → section → paragraph hierarchy
  const byPart = [], seenPart = {}, seenSection = {}, seenPara = {}, seenClause = {};

  const ensurePart = r => {
    if (!seenPart[r.part_number]) {
      seenPart[r.part_number] = { part_number: r.part_number, part_title: r.part_title, sections: [] };
      byPart.push(seenPart[r.part_number]);
    }
    return seenPart[r.part_number];
  };
  const ensureSection = (pt, r) => {
    const key = `${r.part_number}__${r.section_title ?? ''}`;
    if (!seenSection[key]) {
      seenSection[key] = { section_title: r.section_title, paragraphs: [] };
      pt.sections.push(seenSection[key]);
    }
    return seenSection[key];
  };
  const ensurePara = (sec, r) => {
    if (!seenPara[r.paragraph_id]) {
      seenPara[r.paragraph_id] = { paragraph_id: r.paragraph_id, paragraph_number: r.paragraph_number,
        paragraph_title: r.paragraph_title, clauses: [] };
      sec.paragraphs.push(seenPara[r.paragraph_id]);
    }
    return seenPara[r.paragraph_id];
  };
  const ensureClause = (pg, clauseId, clauseNum, clauseText) => {
    const key = `${pg.paragraph_id}__${clauseId ?? 'none'}`;
    if (!seenClause[key]) {
      seenClause[key] = { clause_id: clauseId, clause_number: clauseNum, clause_text: clauseText, clauseReview: null, facts: [] };
      pg.clauses.push(seenClause[key]);
    }
    return seenClause[key];
  };

  filteredClause.forEach(cr => { const pt = ensurePart(cr); const sec = ensureSection(pt, cr); const pg = ensurePara(sec, cr); const cl = ensureClause(pg, cr.clause_id, cr.clause_number, cr.clause_text); cl.clauseReview = cr; });
  filteredFact.forEach(r   => { const pt = ensurePart(r);  const sec = ensureSection(pt, r);  const pg = ensurePara(sec, r);  const cl = ensureClause(pg, r.clause_id, r.clause_number, r.clause_text); cl.facts.push(r); });

  const totalReviews   = factReviews.length + clauseReviews.length;
  const visibleReviews = filteredFact.length + filteredClause.length;
  const totalParas     = Object.keys(seenPara).length;

  const FilterBtn = ({ active, onClick, children }) => (
    <button className={`review-filter-btn${active ? ' active' : ''}`} onClick={onClick}>{children}</button>
  );
  const clearAll = () => { setFilterType(''); setFilterImpact(''); setFilterReviewer(''); setFilterPart(''); setFilterSection(''); };
  const anyFilter = filterType || filterImpact || filterReviewer || filterPart || filterSection;

  return (
    <div className="main">
      <div className="search-results">
        <div className="review-filters review-filters-dropdowns">
          <select className="review-filter-select" value={filterPart}
            onChange={e => { setFilterPart(e.target.value); setFilterSection(''); }}>
            <option value="">All Parts</option>
            {partOptions.map(p => {
              const c = partMap[p];
              return <option key={p} value={p}>{p} — {c.part_title} ({c.human}H / {c.ai}AI)</option>;
            })}
          </select>
          {filterPart && sectionOptions.length > 0 && (
            <select className="review-filter-select" value={filterSection}
              onChange={e => setFilterSection(e.target.value)}>
              <option value="">All Sections</option>
              {sectionOptions.map(s => {
                const c = sectionCountMap[`${filterPart}__${s}`] || { human: 0, ai: 0 };
                return <option key={s} value={s}>{s} ({c.human}H / {c.ai}AI)</option>;
              })}
            </select>
          )}
        </div>
        <div className="review-filters">
          <span className="review-filter-label">Type</span>
          {['Problem','Question','Note'].map(t => (
            <FilterBtn key={t} active={filterType === t} onClick={() => setFilterType(f => f === t ? '' : t)}>{t}</FilterBtn>
          ))}
          <span className="review-filter-sep"/>

          <span className="review-filter-label">Impact</span>
          {['High','Medium','Low','='].map(i => (
            <FilterBtn key={i} active={filterImpact === i} onClick={() => setFilterImpact(f => f === i ? '' : i)}>{i}</FilterBtn>
          ))}
          <span className="review-filter-sep"/>

          <span className="review-filter-label">Reviewer</span>
          {[['Human','Human'],['AI','AI']].map(([val, label]) => (
            <FilterBtn key={val} active={filterReviewer === val} onClick={() => setFilterReviewer(f => f === val ? '' : val)}>{label}</FilterBtn>
          ))}

          {anyFilter && <button className="review-filter-clear" onClick={clearAll}>Clear all</button>}
        </div>

        <h2>Review comments — {visibleReviews === totalReviews ? totalReviews : `${visibleReviews} of ${totalReviews}`} review{totalReviews !== 1 ? 's' : ''} across {totalParas} paragraph{totalParas !== 1 ? 's' : ''}</h2>

        {byPart.map(pt => (
          <div key={pt.part_number} className="review-part-group">
            <h3 className="review-part-heading">{pt.part_number} — {pt.part_title}</h3>
            {pt.sections.map((sec, si) => (
              <div key={si} className="review-section-group">
                {sec.section_title && <h4 className="review-section-heading">{sec.section_title}</h4>}
                {sec.paragraphs.map(pg => (
                  <div key={pg.paragraph_id} className="review-para-group">
                    <div className="review-para-heading">
                      <span className="review-para-title">§{pg.paragraph_number} — {pg.paragraph_title}</span>
                    </div>
                    {pg.clauses.map((cl, ci) => (
                      <div key={ci} className="review-clause-group">
                        {cl.clause_number && (
                          <div className="review-clause-ref">
                            <span className="review-clause-num">({cl.clause_number})</span>
                            {cl.clause_text && <span className="review-clause-text">{cl.clause_text}</span>}
                          </div>
                        )}
                        {cl.clauseReview && (
                          <div className="review-summary-card">
                            <div className="review-summary-fact">
                              <span className="tag clause">clause</span>
                              <span style={{color:'var(--muted)',fontSize:'0.85rem'}}> review of clause ({cl.clause_number})</span>
                            </div>
                            <ReviewBlock
                              review={{ comment: cl.clauseReview.comment, impact: cl.clauseReview.impact, type: cl.clauseReview.type, updated_at: cl.clauseReview.updated_at, ai_generated: cl.clauseReview.ai_generated }}
                              onSave={(comment, impact, type) => saveClauseReview(cl.clauseReview.clause_id, comment, impact, type)}
                              onSaved={result => handleClauseReviewSaved(cl.clauseReview.clause_id, result)}
                              label="clause review" loggedIn={loggedIn}
                              onAccept={() => acceptClauseReview(cl.clauseReview.clause_id)}
                            />
                          </div>
                        )}
                        {cl.facts.map(f => (
                          <div key={f.fact_id} className="review-summary-card">
                            <div className="review-summary-fact">
                              <span className={`tag ${f.provision_type === 'sub-point' ? 'subpoint' : 'point'}`}>{f.provision_type}</span>
                              <span className="fact-ident-inline">({f.identifier})</span>{f.fact_text}
                            </div>
                            <ReviewBlock
                              review={{ comment: f.comment, impact: f.impact, type: f.type, updated_at: f.updated_at, ai_generated: f.ai_generated }}
                              onSave={(comment, impact, type) => saveReview(f.fact_id, comment, impact, type)}
                              onSaved={result => handleFactSaved(f.fact_id, result)}
                              loggedIn={loggedIn}
                              onAccept={() => acceptReview(f.fact_id)}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Rich text editor ──────────────────────────────────────────────────────────
function RichTextEditor({ initialHtml, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = initialHtml || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current.innerHTML);
  };

  const Btn = ({ cmd, val, title, children }) => (
    <button className="rte-btn" title={title} onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}>
      {children}
    </button>
  );

  return (
    <div className="rte-wrap">
      <div className="rte-toolbar">
        <Btn cmd="bold"    title="Bold"><strong>B</strong></Btn>
        <Btn cmd="italic"  title="Italic"><em>I</em></Btn>
        <Btn cmd="underline" title="Underline"><u>U</u></Btn>
        <span className="rte-sep" />
        <Btn cmd="formatBlock" val="h3" title="Heading">H3</Btn>
        <Btn cmd="formatBlock" val="h4" title="Sub-heading">H4</Btn>
        <Btn cmd="formatBlock" val="p"  title="Paragraph">¶</Btn>
        <span className="rte-sep" />
        <Btn cmd="insertUnorderedList" title="Bullet list">• List</Btn>
        <Btn cmd="insertOrderedList"   title="Numbered list">1. List</Btn>
        <span className="rte-sep" />
        <Btn cmd="removeFormat" title="Clear formatting">Clear</Btn>
      </div>
      <div
        ref={editorRef}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current.innerHTML)}
      />
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────
function CollapsibleSection({ title, children, defaultOpen = false, accent = 'var(--primary)' }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cs-wrap" style={{ borderLeftColor: accent }}>
      <div className="cs-header" onClick={() => setOpen(o => !o)}>
        <span className="cs-title">{title}</span>
        <span className="cs-arrow">{open ? '▾' : '▸'}</span>
      </div>
      {open && <div className="cs-body">{children}</div>}
    </div>
  );
}

// ── Analysis Summary ──────────────────────────────────────────────────────────
const SPLIT_MARKER = '<<MAIN-REPORT-START>>';

const splitBySections = (text) => {
  if (!text) return [];
  const lines = text.split('\n');
  const sections = [];
  let title = null, acc = [];
  for (const line of lines) {
    if (line.startsWith('## ')) {
      if (title !== null) sections.push({ title, content: acc.join('\n').trim() });
      title = line.slice(3).trim(); acc = [];
    } else { acc.push(line); }
  }
  if (title !== null) sections.push({ title, content: acc.join('\n').trim() });
  if (!sections.length && text.trim()) return [{ title: 'Report', content: text.trim() }];
  return sections;
};

function AnalysisSummary({ loggedIn }) {
  const [content,     setContent]     = useState(null);
  const [updatedAt,   setUpdatedAt]   = useState(null);
  const [streaming,   setStreaming]   = useState(false);
  const [streamText,  setStreamText]  = useState('');
  const [statusMsg,   setStatusMsg]   = useState('');
  const [editing,     setEditing]     = useState(false);
  const [draft,       setDraft]       = useState('');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [execHtml,    setExecHtml]    = useState('');
  const [editingExec, setEditingExec] = useState(false);
  const [savingExec,  setSavingExec]  = useState(false);
  const [error,       setError]       = useState('');
  const [principles,  setPrinciples]  = useState([]);
  const execDraftRef = useRef('');

  useEffect(() => {
    Promise.all([getAnalysis(), getExecSummary(), getPrinciples()])
      .then(([a, e, ps]) => { setContent(a.content); setUpdatedAt(a.updated_at); setExecHtml(e.content || ''); setPrinciples(ps); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setStreaming(true); setStreamText(''); setStatusMsg(''); setError(''); setEditing(false); setEditingExec(false);
    try {
      const response = await authFetch('/api/analysis/generate', { method: 'POST' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '', fullText = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n'); buffer = parts.pop();
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(trimmed.slice(6));
            if (data.text !== undefined) { fullText += data.text; setStreamText(fullText); setStatusMsg(''); }
            else if (data.status) { setStatusMsg(data.status); }
            else if (data.reset)  { fullText = ''; setStreamText(''); }
            else if (data.done)   { setContent(fullText); setUpdatedAt(new Date().toISOString()); }
            else if (data.error)  { setError(data.error); }
          } catch {}
        }
      }
    } catch (e) { setError(e.message); }
    finally     { setStreamText(''); setStreaming(false); }
  };

  const saveReport = async () => {
    setSaving(true); setError('');
    try {
      const result = await saveAnalysis(draft);
      setContent(result.content); setUpdatedAt(result.updated_at); setEditing(false);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const saveExec = async () => {
    setSavingExec(true); setError('');
    try {
      const result = await saveExecSummary(execDraftRef.current);
      setExecHtml(result.content); setEditingExec(false);
    } catch (e) { setError(e.message); }
    finally { setSavingExec(false); }
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('*')  && p.endsWith('*') && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
      if (p.startsWith('`')  && p.endsWith('`'))  return <code key={i}>{p.slice(1, -1)}</code>;
      return p;
    });
  };

  const renderMarkdown = (text) => {
    const lines = (text || '').split('\n');
    const out = []; let list = null; let k = 0;
    const flush = () => { if (list) { out.push(<ul key={k++} className="analysis-ul">{list}</ul>); list = null; } };
    for (const line of lines) {
      if      (line.startsWith('#### ')) { flush(); out.push(<h5 key={k++} className="analysis-h5">{renderInline(line.slice(5))}</h5>); }
      else if (line.startsWith('### '))  { flush(); out.push(<h4 key={k++} className="analysis-h4">{renderInline(line.slice(4))}</h4>); }
      else if (line.startsWith('## '))   { flush(); out.push(<h3 key={k++} className="analysis-h3">{renderInline(line.slice(3))}</h3>); }
      else if (line.startsWith('# '))    { flush(); out.push(<h2 key={k++} className="analysis-h2">{renderInline(line.slice(2))}</h2>); }
      else if (line.startsWith('- ') || line.startsWith('* ')) { if (!list) list = []; list.push(<li key={k++}>{renderInline(line.slice(2))}</li>); }
      else if (line.trim() === '') { flush(); }
      else { flush(); out.push(<p key={k++} className="analysis-p">{renderInline(line)}</p>); }
    }
    flush(); return out;
  };

  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  const splitReport = (text) => {
    if (!text) return { intro: '', main: '' };
    const idx = text.indexOf(SPLIT_MARKER);
    if (idx === -1) return { intro: text, main: '' };
    return { intro: text.slice(0, idx).trim(), main: text.slice(idx + SPLIT_MARKER.length).trim() };
  };

  if (loading) return <div className="main"><p className="loading">Loading…</p></div>;

  const { intro, main } = splitReport(content);

  return (
    <div className="main">
      <div className="analysis-container">
        <div className="analysis-header">
          <h2 className="analysis-title">Analysis Summary</h2>
          <div className="analysis-actions">
            {loggedIn && !streaming && (
              <button className="btn-primary btn-sm" onClick={generate}>
                {content ? 'Regenerate report' : 'Generate report'}
              </button>
            )}
            {loggedIn && content && !streaming && !editing && (
              <button className="btn-ghost btn-sm" onClick={() => { setDraft(content); setEditing(true); }}>Edit AI report</button>
            )}
            {editing && (
              <>
                <button className="btn-primary btn-sm" onClick={saveReport} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button className="btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>
        {updatedAt && !editing && <div className="analysis-meta">Last generated {fmtDate(updatedAt)}</div>}
        {error && <div className="analysis-error">{error}</div>}

        {/* Streaming view — full unsplit text */}
        {streaming && !streamText && (
          <div className="analysis-body">
            <p className="loading">Generating analysis…</p>
            {statusMsg && <p className="analysis-status">{statusMsg}</p>}
          </div>
        )}
        {streaming && streamText && <div className="analysis-body analysis-streaming">{renderMarkdown(streamText)}</div>}

        {/* Edit raw AI report */}
        {!streaming && editing && (
          <textarea className="analysis-textarea" value={draft} onChange={e => setDraft(e.target.value)} />
        )}

        {/* Normal split view */}
        {!streaming && !editing && (
          <>
            {/* Part 1: Act title + Overview — always visible */}
            {intro
              ? <div className="analysis-body">{renderMarkdown(intro)}</div>
              : <div className="analysis-body"><p className="placeholder">{loggedIn ? 'Click "Generate report" to create an AI analysis of all review comments.' : 'No analysis report has been generated yet.'}</p></div>
            }

            {/* Review Principles — collapsed by default */}
            {principles.length > 0 && (
              <CollapsibleSection title="Review Principles" accent="var(--primary)">
                <p className="cs-intro-text">These principles form a checklist for the regulatory assessment and are designed to evaluate laws, regulations, and policies. They prioritise human rights (privacy, free expression, association, and self-determination) through technical architecture.</p>
                <div className="principles-display-list cs-principles">
                  {principles.map(p => (
                    <div key={p.principle_id} className="principle-display-item">
                      <div className="principle-display-header">
                        <span className="principle-display-title">{p.title}</span>
                        <span className={`principle-badge prio-${p.priority.toLowerCase()}`}>{p.priority}</span>
                      </div>
                      {p.description && <div className="principle-display-desc">{p.description}</div>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Review and Analysis Approach — collapsed by default */}
            <CollapsibleSection title="Review and Analysis Approach" accent="var(--muted)">
              <p className="cs-intro-text">All the clauses and points from the DUAA were reviewed against the Architecture Principles. The review tries to identify where the Act potentially conflicts with the stated Principles. The review was carried out in two stages, firstly an AI sweep using the latest Claude LLM, and secondly a series of detailed manual reviews by technical experts in the field of data management and privacy. The output of the review is a list of concerns grouped into 'themes' along with a list of risks and actions.</p>
            </CollapsibleSection>

            {/* Executive Summary — collapsed by default */}
            <CollapsibleSection title="Executive Summary" accent="var(--accent)">
              {loggedIn && (
                <div className="cs-toolbar">
                  {!editingExec
                    ? <button className="btn-ghost btn-sm" onClick={() => { execDraftRef.current = execHtml; setEditingExec(true); }}>
                        {execHtml ? 'Edit' : 'Add content'}
                      </button>
                    : <>
                        <button className="btn-primary btn-sm" onClick={saveExec} disabled={savingExec}>{savingExec ? 'Saving…' : 'Save'}</button>
                        <button className="btn-ghost btn-sm" onClick={() => setEditingExec(false)}>Cancel</button>
                      </>
                  }
                </div>
              )}
              {editingExec
                ? <RichTextEditor initialHtml={execHtml} onChange={html => { execDraftRef.current = html; }} />
                : <div className="rte-body" dangerouslySetInnerHTML={{ __html: execHtml || `<p class="rte-placeholder">${loggedIn ? 'Click "Add content" to write an executive summary.' : 'No executive summary has been added yet.'}</p>` }} />
              }
            </CollapsibleSection>

            {/* Main report sections — each collapsed by default */}
            {main && splitBySections(main).map((sec, idx) => (
              <CollapsibleSection key={idx} title={sec.title || 'Report Section'}>
                <div className="analysis-body">{renderMarkdown(sec.content)}</div>
              </CollapsibleSection>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Principle form ────────────────────────────────────────────────────────────
function PrincipleForm({ principle, onSave, onCancel }) {
  const isNew = !principle;
  const [title,    setTitle]    = useState(principle?.title       ?? '');
  const [desc,     setDesc]     = useState(principle?.description ?? '');
  const [priority, setPriority] = useState(principle?.priority    ?? 'Medium');
  const [sortOrd,  setSortOrd]  = useState(String(principle?.sort_order ?? 0));
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  const handleSave = async () => {
    if (!title.trim()) return setErr('Title required');
    setSaving(true); setErr('');
    try {
      const data = { title: title.trim(), description: desc.trim(), priority, sort_order: parseInt(sortOrd) || 0 };
      const result = isNew ? await createPrinciple(data) : await updatePrinciple(principle.principle_id, data);
      onSave(result);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="principle-form">
      <div className="edit-form-title">{isNew ? 'Add principle' : `Edit: ${principle.title}`}</div>
      {err && <div className="edit-err">{err}</div>}
      <div className="edit-form-row"><label>Title</label>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Principle title" /></div>
      <div className="edit-form-row"><label>Description</label>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe what this principle means for your organisation…" /></div>
      <div className="edit-form-row"><label>Priority</label>
        <select value={priority} onChange={e => setPriority(e.target.value)} style={{maxWidth:140}}>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select></div>
      <div className="edit-form-row"><label>Sort order</label>
        <input type="number" value={sortOrd} onChange={e => setSortOrd(e.target.value)} style={{maxWidth:80}} /></div>
      <div className="edit-form-actions">
        <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : isNew ? 'Add' : 'Save'}</button>
        <button className="btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Principles page ───────────────────────────────────────────────────────────
function PrinciplesPage({ loggedIn, generating, genLog, genResult, genError, onGenerate }) {
  const [principles,  setPrinciples]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [editing,     setEditing]     = useState(null); // principle_id or 'new'
  const [error,       setError]       = useState('');

  useEffect(() => {
    getPrinciples().then(ps => { setPrinciples(ps); setLoading(false); });
  }, []);

  const handleSaved = (result) => {
    setPrinciples(prev => {
      const exists = prev.some(p => p.principle_id === result.principle_id);
      return exists ? prev.map(p => p.principle_id === result.principle_id ? result : p) : [...prev, result];
    });
    setEditing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this principle? All AI-generated review comments will be cleared.')) return;
    try {
      await deletePrinciple(id);
      setPrinciples(prev => prev.filter(p => p.principle_id !== id));
    } catch (e) { setError(e.message); }
  };


  if (!loggedIn) return <div className="main"><p className="placeholder">Login to manage principles.</p></div>;
  if (loading)   return <div className="main"><p className="loading">Loading…</p></div>;

  const priorityOrder = { High: 0, Medium: 1, Low: 2 };

  return (
    <div className="main">
      <div className="principles-page">
        <div className="principles-header">
          <h2 className="principles-title">Review Principles</h2>
          <div className="principles-header-actions">
            {editing !== 'new' && (
              <button className="btn-primary btn-sm" onClick={() => setEditing('new')}>+ Add principle</button>
            )}
            <button className="btn-generate" onClick={onGenerate} disabled={generating || principles.length === 0}
              title={principles.length === 0 ? 'Add at least one principle first' : 'Analyse the Act against these principles and generate AI review comments'}>
              {generating ? 'Generating…' : '⚡ Generate AI reviews'}
            </button>
          </div>
        </div>
        <p className="principles-intro">
          Define the principles your organisation uses to assess legislation. The AI will compare every clause and provision against these principles and flag potential concerns as review comments.
          Changing principles will clear all existing AI-generated review comments.
        </p>

        {(error || genError) && <div className="analysis-error">{error || genError}</div>}

        {/* Generation log */}
        {(genLog.length > 0 || genResult) && (
          <div className="gen-log">
            {genLog.map((msg, i) => <div key={i} className="gen-log-line">{msg}</div>)}
            {genResult && (
              <div className="gen-log-result">
                Done — {genResult.created} AI review comment{genResult.created !== 1 ? 's' : ''} created
                {genResult.skipped > 0 ? `, ${genResult.skipped} skipped` : ''}.
              </div>
            )}
          </div>
        )}

        {/* Add form */}
        {editing === 'new' && (
          <PrincipleForm onSave={handleSaved} onCancel={() => setEditing(null)} />
        )}

        {/* Principles list */}
        {principles.length === 0 && editing !== 'new' && (
          <p className="empty">No principles defined yet. Add one to get started.</p>
        )}
        {[...principles].sort((a,b) => (priorityOrder[a.priority]??2) - (priorityOrder[b.priority]??2) || a.sort_order - b.sort_order).map(p => (
          <div key={p.principle_id}>
            {editing === p.principle_id ? (
              <PrincipleForm principle={p} onSave={handleSaved} onCancel={() => setEditing(null)} />
            ) : (
              <div className="principle-card">
                <div className="principle-card-header">
                  <span className="principle-card-title">{p.title}</span>
                  <span className={`principle-badge prio-${p.priority.toLowerCase()}`}>{p.priority}</span>
                  <button className="edit-btn" onClick={() => setEditing(p.principle_id)}>✎</button>
                  <button className="btn-danger" onClick={() => handleDelete(p.principle_id)}>Delete</button>
                </div>
                {p.description && <div className="principle-card-desc">{p.description}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({
  parts, sections, flatParagraphs, paragraphs,
  selectedSection, selectedParagraph, onSelectSection, onSelectParagraph,
  open = true,
  mode, onModeChange, searchTerm, onSearchChange, onSearch,
  authed, unreadCount, onAbout, onContact, onHome,
  itemTagsMap, taggedPartIds, taggedSectionIds,
}) {
  const [expandedParts,    setExpandedParts]    = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [partsOpen,        setPartsOpen]        = useState(true);

  const taggedParagraphs = useMemo(() => new Set(
    Object.entries(itemTagsMap)
      .filter(([k, v]) => k.startsWith('paragraph:') && v.length > 0)
      .map(([k]) => parseInt(k.split(':')[1]))
  ), [itemTagsMap]);

  const togglePart = id => setExpandedParts(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const toggleSection = sec => {
    onSelectSection(sec);
    setExpandedSections(prev => { const next = new Set(prev); next.has(sec.section_id) ? next.delete(sec.section_id) : next.add(sec.section_id); return next; });
  };

  return (
    <nav className={`sidebar${open ? '' : ' sidebar-closed'}`}>

      {/* ── Actions ── */}
      <div className="sb-section-label">Actions</div>
      <button className={`sb-action-btn${mode === 'analysis' ? ' sba-analysis' : ''}`}
        onClick={() => onModeChange(mode === 'analysis' ? 'browse' : 'analysis')}>
        Analysis Report
      </button>
      <button className={`sb-action-btn${mode === 'reviews' ? ' sba-reviews' : ''}`}
        onClick={() => onModeChange(mode === 'reviews' ? 'browse' : 'reviews')}>
        Review Comments
      </button>
      {authed && (
        <>
          <button className={`sb-action-btn${mode === 'principles' ? ' sba-principles' : ''}`}
            onClick={() => onModeChange(mode === 'principles' ? 'browse' : 'principles')}>
            Principles
          </button>
          <button className={`sb-action-btn${mode === 'messages' ? ' sba-messages' : ''}`}
            onClick={() => onModeChange(mode === 'messages' ? 'browse' : 'messages')}>
            Messages{unreadCount > 0 && <span className="msg-badge">{unreadCount}</span>}
          </button>
        </>
      )}

      {/* ── DUAA Parts ── */}
      <div className="sb-parts-label" onClick={() => setPartsOpen(o => !o)}>
        <span>DUAA Parts</span>
        <span className="arrow">{partsOpen ? '▾' : '▸'}</span>
      </div>

      {partsOpen && (
        <div className="sb-search-row">
          <input className="sb-search-input" type="text" placeholder="Search clauses…"
            value={searchTerm} onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearch()} />
          <button className="sb-search-btn" onClick={onSearch}>Go</button>
        </div>
      )}

      {partsOpen && parts.map(part => {
        const partOpen     = expandedParts.has(part.part_id);
        const partSections = sections[part.part_id] || [];
        const isFlat       = partSections.length === 0;
        return (
          <div key={part.part_id}>
            <div className="sb-part-header sb-clickable" onClick={() => togglePart(part.part_id)}>
              <div className="sb-part-row">
                <div>
                  <div className="sb-part-num">{part.part_number}</div>
                  <div className="sb-part-title">{part.part_title}</div>
                </div>
                <span className="sb-part-right">
                  {authed && taggedPartIds.has(part.part_id) && <span className="sb-tag-dot" title="Contains tagged items" />}
                  <span className="arrow">{partOpen ? '▾' : '▸'}</span>
                </span>
              </div>
            </div>
            {partOpen && !isFlat && partSections.map(sec => {
              const secOpen = expandedSections.has(sec.section_id);
              return (
                <div key={sec.section_id}>
                  <div className={`sb-section${selectedSection?.section_id === sec.section_id ? ' active' : ''}`}
                    onClick={() => toggleSection(sec)}>
                    <span className="sb-section-text">{sec.section_title}</span>
                    <span className="sb-section-right">
                      {authed && taggedSectionIds.has(sec.section_id) && <span className="sb-tag-dot" title="Contains tagged items" />}
                      <span className="arrow">{secOpen ? '▾' : '▸'}</span>
                    </span>
                  </div>
                  {secOpen && (paragraphs[sec.section_id] || []).map(para => (
                    <div key={para.paragraph_id}
                      className={`sb-para${selectedParagraph?.paragraph_id === para.paragraph_id ? ' active' : ''}`}
                      onClick={() => onSelectParagraph(para)}>
                      {para.paragraph_number}. {para.paragraph_title}
                      {authed && taggedParagraphs.has(para.paragraph_id) && <span className="sb-tag-dot" title="Contains tagged items" />}
                    </div>
                  ))}
                </div>
              );
            })}
            {partOpen && isFlat && (flatParagraphs[part.part_id] || []).map(para => (
              <div key={para.paragraph_id}
                className={`sb-para${selectedParagraph?.paragraph_id === para.paragraph_id ? ' active' : ''}`}
                onClick={() => onSelectParagraph(para)}>
                {para.paragraph_number}. {para.paragraph_title}
                {authed && taggedParagraphs.has(para.paragraph_id) && <span className="sb-tag-dot" title="Contains tagged items" />}
              </div>
            ))}
          </div>
        );
      })}

      {/* ── Links ── */}
      <div className="sb-section-label">Links</div>
      <button className="sb-link-btn" onClick={onAbout}>About this app</button>
      <button className={`sb-link-btn${mode === 'pub-principles' ? ' sb-link-active' : ''}`}
        onClick={() => onModeChange('pub-principles')}>Principles</button>
      <button className="sb-link-btn" onClick={onContact}>Contact / Feedback</button>
      <button className={`sb-link-btn${mode === 'browse' && !selectedParagraph ? ' sb-link-active' : ''}`} onClick={onHome}>Landing page</button>
      <a className="sb-action-link"
         href="https://www.legislation.gov.uk/ukpga/2025/18/enacted"
         target="_blank" rel="noopener noreferrer">
        Full Act on legislation.gov.uk ↗
      </a>
    </nav>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [authed,    setAuthed]    = useState(false);
  const [pubkey,    setPubkey]    = useState('');
  const [checking,  setChecking]  = useState(true);
  const [showLogin,   setShowLogin]   = useState(false);
  const [showAbout,   setShowAbout]   = useState(false);
  const [showContact, setShowContact] = useState(false);

  const [parts,          setParts]          = useState([]);
  const [sections,       setSections]       = useState({});
  const [flatParagraphs, setFlatParagraphs] = useState({});
  const [paragraphs,     setParagraphs]     = useState({});
  const [selSection,   setSelSection]   = useState(null);
  const [selParagraph, setSelParagraph] = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [searchRes,    setSearchRes]    = useState(null);
  const [searching,    setSearching]    = useState(false);
  const [mode,         setMode]         = useState('browse');
  const [editMode,     setEditMode]     = useState(false);
  const [sidebarOpen,  setSidebarOpen]  = useState(true);

  const [unreadCount,  setUnreadCount]  = useState(0);

  const [tags,           setTags]           = useState([]);
  const [rawItemTagRows, setRawItemTagRows] = useState([]);

  // Everything derived from rawItemTagRows — single source of truth
  const itemTagsMap = useMemo(() => {
    const map = {};
    for (const r of rawItemTagRows) {
      const key = `${r.item_type}:${r.item_id}`;
      if (!map[key]) map[key] = [];
      map[key].push({ tag_id: r.tag_id, name: r.name, colour: r.colour });
    }
    return map;
  }, [rawItemTagRows]);

  const taggedPartIds = useMemo(() =>
    new Set(rawItemTagRows.map(r => r.part_id ? parseInt(r.part_id) : null).filter(Boolean))
  , [rawItemTagRows]);

  const taggedSectionIds = useMemo(() =>
    new Set(rawItemTagRows.map(r => r.section_id ? parseInt(r.section_id) : null).filter(Boolean))
  , [rawItemTagRows]);

  // AI review generation — lifted here so state survives navigation away from Principles
  const [aiGenRunning, setAiGenRunning] = useState(false);
  const [aiGenLog,     setAiGenLog]     = useState([]);
  const [aiGenResult,  setAiGenResult]  = useState(null);
  const [aiGenError,   setAiGenError]   = useState('');

  const refreshItemTags = () =>
    getItemTags().then(rows => setRawItemTagRows(rows)).catch(() => {});

  const handleItemTagsChanged = (itemKey, tagRows, newAllTags) => {
    const [itemType, itemIdStr] = itemKey.split(':');
    const itemId = parseInt(itemIdStr);
    // Optimistic update: remove old rows for this item, add new ones (without part/section ids)
    setRawItemTagRows(prev => {
      const filtered = prev.filter(r => !(r.item_type === itemType && parseInt(r.item_id) === itemId));
      const newRows = tagRows.map(t => ({ ...t, item_type: itemType, item_id: itemId }));
      return [...filtered, ...newRows];
    });
    if (newAllTags) setTags(newAllTags);
    // Refresh to restore part_id/section_id on new rows (for hierarchy dots)
    refreshItemTags();
  };

  // Check existing token on mount
  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    authFetch('/api/auth/me')
      .then(r => r.json())
      .then(d => { if (d.pubkey) { setAuthed(true); setPubkey(d.pubkey); getUnreadCount().then(r => setUnreadCount(r.count)).catch(() => {}); } })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  // Load sidebar data (public — no auth needed)
  useEffect(() => {
    getParts().then(async ps => {
      setParts(ps);
      const sMap = {};
      await Promise.all(ps.map(async p => { sMap[p.part_id] = await getSections(p.part_id); }));
      setSections(sMap);
      const fpMap = {};
      await Promise.all(ps.filter(p => sMap[p.part_id].length === 0).map(async p => {
        fpMap[p.part_id] = await getPartParagraphs(p.part_id);
      }));
      setFlatParagraphs(fpMap);
    });
    getTags().then(setTags).catch(() => {});
    getItemTags().then(rows => setRawItemTagRows(rows)).catch(() => {});
  }, []);

  const mountedRef = useRef(false);

  const restoreFromHash = useCallback(async (hash) => {
    const knownModes = ['analysis', 'reviews', 'principles', 'pub-principles', 'messages'];
    if (!hash) {
      setMode('browse'); setSelParagraph(null); setSelSection(null);
    } else if (knownModes.includes(hash)) {
      setMode(hash); setSelParagraph(null); setSelSection(null);
    } else if (hash.startsWith('paragraph/')) {
      const paraId = parseInt(hash.split('/')[1]);
      if (!paraId) return;
      try {
        const data = await getParagraphById(paraId);
        setSelParagraph(data);
        setSelSection(data.section_id
          ? { section_id: data.section_id, section_title: data.section_title, part_id: data.part_id }
          : null);
        setMode('browse');
      } catch {}
    }
  }, []);

  // Sync URL hash when mode or selected paragraph changes (skip initial render)
  useEffect(() => {
    if (!mountedRef.current) return;
    const hash = (mode === 'browse' && selParagraph)
      ? `paragraph/${selParagraph.paragraph_id}`
      : mode !== 'browse' ? mode : '';
    if (hash) {
      window.history.replaceState(null, '', `#${hash}`);
    } else {
      window.history.replaceState(null, '', window.location.pathname || '/');
    }
  }, [mode, selParagraph]);

  // Read initial hash on mount and listen for back/forward navigation
  useEffect(() => {
    const initialHash = window.location.hash.slice(1);
    if (initialHash) restoreFromHash(initialHash);
    const onHashChange = () => restoreFromHash(window.location.hash.slice(1));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [restoreFromHash]);

  // Mark mounted — defined AFTER hash sync effect so the initial render skips the sync
  useEffect(() => { mountedRef.current = true; }, []);

  const handleLoginSuccess = async () => {
    const d = await authFetch('/api/auth/me').then(r => r.json());
    setPubkey(d.pubkey); setAuthed(true);
    getUnreadCount().then(r => setUnreadCount(r.count)).catch(() => {});
  };

  const refreshUnreadCount = () =>
    getUnreadCount().then(r => setUnreadCount(r.count)).catch(() => {});

  const handleLogout = () => {
    logout(); setAuthed(false); setPubkey(''); setEditMode(false);
  };

  const handleGenerateAiReviews = async () => {
    setAiGenRunning(true); setAiGenLog([]); setAiGenResult(null); setAiGenError('');
    try {
      const response = await authFetch('/api/ai-reviews/generate', { method: 'POST' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n'); buffer = parts.pop();
        for (const part of parts) {
          const trimmed = part.trim();
          if (trimmed.startsWith(':')) continue;
          if (!trimmed.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(trimmed.slice(6));
            if (data.status) setAiGenLog(prev => [...prev, data.status]);
            else if (data.done)  setAiGenResult({ created: data.created, skipped: data.skipped });
            else if (data.error) setAiGenError(data.error);
          } catch {}
        }
      }
    } catch (e) { setAiGenError(e.message); }
    finally { setAiGenRunning(false); }
  };

  const handleSelectSection = useCallback(async sec => {
    setSelSection(sec); setSelParagraph(null); setMode('browse');
    if (!paragraphs[sec.section_id]) {
      const paras = await getParagraphs(sec.section_id);
      setParagraphs(prev => ({ ...prev, [sec.section_id]: paras }));
    }
  }, [paragraphs]);

  const handleSearch = useCallback(async () => {
    if (searchTerm.trim().length < 3) return;
    setMode('search'); setSearching(true);
    setSearchRes(await search(searchTerm.trim()));
    setSearching(false);
  }, [searchTerm]);

  const partById = id => parts.find(p => p.part_id === id);
  const shortKey = pk => pk ? `${pk.slice(0, 8)}…${pk.slice(-4)}` : '';

  if (checking) return null;

  return (
    <>
      <header>
        <button className="header-logo-btn" onClick={() => { setMode('browse'); setSelParagraph(null); setSelSection(null); }} title="Home">
          <img src="/logo192.png" alt="Site logo" className="header-logo" />
        </button>
        <h1 className="header-title" data-short="DUAA Architecture Review">UK Data (Use and Access) Act 2025 — Architecture Review</h1>
        <div className="header-controls">
          <CopyLinkButton />
          {authed && (
            <button className={`btn-edit${editMode ? ' active' : ''}`}
              onClick={() => setEditMode(m => !m)} title="Toggle edit mode">
              Edit data
            </button>
          )}
          {authed ? (
            <>
              <span className="meta">{shortKey(pubkey)}</span>
              <button className="btn-logout" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <button className="btn-login" onClick={() => setShowLogin(true)}>Login</button>
          )}
        </div>
      </header>

      {showLogin   && <LoginModal onLogin={handleLoginSuccess} onClose={() => setShowLogin(false)} />}
      {showAbout   && <AboutModal onClose={() => setShowAbout(false)} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}

      <div className="layout">
        <Sidebar
          parts={parts} sections={sections} flatParagraphs={flatParagraphs} paragraphs={paragraphs}
          selectedSection={selSection} selectedParagraph={selParagraph}
          onSelectSection={handleSelectSection}
          open={sidebarOpen}
          onSelectParagraph={p => {
            setSelParagraph(p); setMode('browse');
            const parentSection = p.section_id != null
              ? Object.values(sections).flat().find(s => s.section_id === p.section_id) ?? null
              : null;
            setSelSection(parentSection);
          }}
          mode={mode} onModeChange={setMode}
          searchTerm={searchTerm} onSearchChange={setSearchTerm} onSearch={handleSearch}
          authed={authed} unreadCount={unreadCount}
          onAbout={() => setShowAbout(true)}
          onContact={() => setShowContact(true)}
          onHome={() => { setMode('browse'); setSelParagraph(null); setSelSection(null); }}
          itemTagsMap={itemTagsMap}
          taggedPartIds={taggedPartIds} taggedSectionIds={taggedSectionIds}
        />
        <button className={`sidebar-toggle${sidebarOpen ? ' sidebar-toggle-open' : ' sidebar-toggle-closed'}`}
          onClick={() => setSidebarOpen(o => !o)}
          title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          {sidebarOpen ? '◀' : '▶'}
        </button>
        {mode === 'search'          ? <SearchResults results={searchRes} loading={searching} /> :
         mode === 'reviews'         ? <AllReviews loggedIn={authed} /> :
         mode === 'analysis'        ? <AnalysisSummary loggedIn={authed} /> :
         mode === 'messages'        ? <MessagesPage onRead={refreshUnreadCount} /> :
         mode === 'pub-principles'  ? <PrinciplesPublicPage onContact={() => setShowContact(true)} /> :
         mode === 'principles' ? <PrinciplesPage loggedIn={authed}
           generating={aiGenRunning} genLog={aiGenLog} genResult={aiGenResult} genError={aiGenError}
           onGenerate={handleGenerateAiReviews} /> :
         <ParagraphDetail
           paragraph={selParagraph} section={selSection}
           part={selParagraph ? partById(selParagraph.part_id) : null}
           editMode={editMode && authed} loggedIn={authed}
           onNav={setMode} onContact={() => setShowContact(true)}
           tags={tags} itemTagsMap={itemTagsMap} onTagsChanged={handleItemTagsChanged}
         />
        }
      </div>
    </>
  );
}
